import { ReactNode, useEffect, useState } from "react";
import { Autocomplete, Box, IconButton, TextField, Tooltip } from "@mui/material";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { WorkshopSession } from "../../types/workshopSession";
import { fetchVehiclesWithUnknownTypes, VehicleUnknownType, changeVinOnVehicle, setCurrentLocation } from "../../api/vehicles";
import { fetchWorkshopAreas, WorkshopArea } from "../../api/workshops";
import { VehiclePositionMap } from "../map/vehiclePositionMap";
import { useSelectedWorkshop } from "../../context/workshopContext";
import View from "../atoms/view";
import { DestinationCard } from "./destinationCard";
import { VehicleInformationCard } from "./vehicleInformationCard";
import { StatusSummaryCard } from "./statusSummaryCard";
import EditVehicleInformationModal, { EditedVehicleInformation } from "./editVehicleInformationModal";
import VinVerificationModal from "./vinVerificationModal";
import { formatDateTime } from "../timeAgo";
import { Colors } from "../../constants/theme";

const styles = {
  column: {
    flex: 1,
    minWidth: 0,
    display: "flex",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    gap: "24px",
    width: "100%",
  },
};

const iconSx = { fontSize: 20, color: Colors.light.green };

const formatLabel = (text = "") =>
  text
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const formatVoltage = (value?: number) =>
  value === undefined || value === null ? "—" : `${value} V`;

const getDamageTitle = (section?: string, area?: string) =>
  [formatLabel(section), formatLabel(area)].filter(Boolean).join(" ");

const isMissingVehicleField = (value?: string | number | null) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return !normalized || normalized === "unknown" || normalized === "-";
};

const missingFieldSx = {
  fontSize: 14,
  fontStyle: "italic",
  fontWeight: 400,
  color: Colors.light.black,
};

const formatHeadlinePart = (value: string | number | undefined | null, missingLabel: string) =>
  isMissingVehicleField(value) ? (
    <Box component="span" sx={missingFieldSx}>
      {missingLabel}
    </Box>
  ) : (
    value
  );

const formatVehicleHeadline = (vehicle?: { make?: string; model?: string; year?: number } | null): ReactNode => (
  <>
    {formatHeadlinePart(vehicle?.make, "Missing make")}
    {", "}
    {formatHeadlinePart(vehicle?.model, "Missing model")}
    {", "}
    {formatHeadlinePart(vehicle?.year, "Missing year")}
  </>
);

const normalizeVin = (value?: string | null) => String(value ?? "").trim().toUpperCase();

export const VehicleOverview = ({
  vin,
  currentWorkshopSession,
  onViewAllDamages,
  onSaved,
}: {
  vin: string | undefined;
  currentWorkshopSession?: WorkshopSession | null;
  onViewAllDamages?: () => void;
  onSaved?: (saved: EditedVehicleInformation) => void;
}) => {
  const { selectedOrganisationId: workshopId } = useSelectedWorkshop();
  const [editOpen, setEditOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [unmatchedVins, setUnmatchedVins] = useState<VehicleUnknownType[]>([]);
  const [areas, setAreas] = useState<WorkshopArea[]>([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [selectedArea, setSelectedArea] = useState('');
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const ongoingTransfer = currentWorkshopSession?.ongoingTransfer;
  const vehicle = currentWorkshopSession?.vehicle;
  const vehicleHeadline = formatVehicleHeadline(vehicle);
  const displayVin = vehicle?.vin || vin;
  const currentLocation = currentWorkshopSession?.currentLocation ?? currentWorkshopSession?.area ?? '';
  const hasLocationChanged = selectedArea.trim() !== currentLocation.trim();
  const canSaveLocation = Boolean(
    currentWorkshopSession?.workSessionId && selectedArea.trim() && hasLocationChanged && !isSavingLocation
  );
  const needsVerification = unmatchedVins.some(
    (item) => normalizeVin(item.vin) === normalizeVin(displayVin)
  );

  useEffect(() => {
    if (!workshopId) {
      setUnmatchedVins([]);
      return;
    }

    let cancelled = false;

    const loadUnmatchedVins = async () => {
      try {
        const data = await fetchVehiclesWithUnknownTypes(workshopId);
        if (!cancelled) {
          setUnmatchedVins((data ?? []).filter((item) => item?.vin));
        }
      } catch (error) {
        console.error(error);
      }
    };

    loadUnmatchedVins();

    return () => {
      cancelled = true;
    };
  }, [workshopId]);

  useEffect(() => {
    if (!workshopId) {
      setAreas([]);
      setIsLoadingAreas(false);
      return;
    }

    let cancelled = false;

    const loadAreas = async () => {
      setIsLoadingAreas(true);
      try {
        const data = await fetchWorkshopAreas(workshopId);
        if (!cancelled) {
          setAreas(data?.items ?? []);
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setAreas([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAreas(false);
        }
      }
    };

    loadAreas();

    return () => {
      cancelled = true;
    };
  }, [workshopId]);

  useEffect(() => {
    const sessionArea = currentWorkshopSession?.currentLocation ?? currentWorkshopSession?.area;
    if (sessionArea) {
      setSelectedArea((current) => current || sessionArea);
    }
  }, [currentWorkshopSession?.currentLocation, currentWorkshopSession?.area]);

  const batteryChecks = [...(currentWorkshopSession?.batteryChecks ?? [])].sort(
    (a, b) =>
      new Date(b.reportedAtLocal).getTime() - new Date(a.reportedAtLocal).getTime(),
  );
  const latestBattery = batteryChecks[0];
  const previousBatteries = batteryChecks.slice(1, 4);

  const damageReports = currentWorkshopSession?.damageReports ?? [];
  const recentDamages = damageReports.slice(0, 3);

  const handleSaveLocation = async () => {
    const workSessionId = currentWorkshopSession?.workSessionId;
    if (!workSessionId || !selectedArea.trim()) {
      return;
    }

    setIsSavingLocation(true);
    try {
      await setCurrentLocation({
        workSessionId,
        currentLocation: selectedArea.trim(),
      });
    } catch (error) {
      console.error('Failed to update vehicle location:', error);
    } finally {
      setIsSavingLocation(false);
    }
  };

  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {ongoingTransfer && (
        <DestinationCard
          isTransferActive
          fromLocation={{ name: ongoingTransfer.startAddress, address: "" }}
          toLocation={{ name: ongoingTransfer.endAddress, address: "" }}
        />
      )}
      <Box
        style={{
          width: "100%",
          height: 250,
          display: "flex",
          alignSelf: "center",
        }}
      >
        <VehiclePositionMap
          latitude={currentWorkshopSession?.geoLocation?.latitude}
          longitude={currentWorkshopSession?.geoLocation?.longitude}
        />
      </Box>

      <View extend={styles.row}>
        <View extend={styles.column}>
          <VehicleInformationCard
            headline={vehicleHeadline}
            details={[
              { label: "VIN", value: displayVin },
              { label: "Key number", value: currentWorkshopSession?.keyNumber },
              {
                label: "Location",
                value: (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      openOnFocus
                      options={areas.map((area) => area.name)}
                      filterOptions={(options) => options}
                      inputValue={selectedArea}
                      loading={isLoadingAreas}
                      onInputChange={(_event, value) => setSelectedArea(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            isLoadingAreas ? "Loading..." : "Select or type area"
                          }
                        />
                      )}
                      sx={{ minWidth: 0, flex: 1 }}
                    />
                    <Tooltip title="Save location">
                      <span>
                        <IconButton
                          color="primary"
                          size="small"
                          aria-label="Save location"
                          disabled={!canSaveLocation}
                          onClick={handleSaveLocation}
                          sx={{ visibility: canSaveLocation ? "visible" : "hidden" }}
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ),
              },
              { label: "License plate", value: vehicle?.licensePlate },
            ]}
            onEdit={() => setEditOpen(true)}
            onVerify={() => setVerifyOpen(true)}
            needsVerification={needsVerification}
          />
        </View>
        <View extend={styles.column}>
          <StatusSummaryCard
            title="Battery status"
            icon={<BatteryChargingFullIcon sx={iconSx} />}
            highlightLabel="Latest check"
            highlightValue={
              latestBattery ? formatVoltage(latestBattery.reportedValue) : null
            }
            highlightDate={
              latestBattery ? formatDateTime(latestBattery.reportedAtLocal) : null
            }
            listLabel="Previous checks"
            items={previousBatteries.map((check, index) => ({
              id: `${check.reportedAtLocal}-${index}`,
              value: formatVoltage(check.reportedValue),
              date: formatDateTime(check.reportedAtLocal),
            }))}
            footerLabel="View battery history"
            emptyMessage="No battery checks reported"
          />
        </View>
        <View extend={styles.column}>
          <StatusSummaryCard
            title="Damages"
            icon={<ReportProblemOutlinedIcon sx={iconSx} />}
            highlightValue={damageReports.length > 0 ? damageReports.length : null}
            highlightSublabel={damageReports.length > 0 ? "reported damages" : undefined}
            items={recentDamages.map((damage, index) => ({
              id: damage.damageId ?? `${damage.section}-${damage.area}-${index}`,
              value: getDamageTitle(damage.section, damage.area),
              date: formatDateTime(damage.reportedAtLocal || damage.created),
            }))}
            footerLabel="View all damages"
            onFooterClick={onViewAllDamages}
            emptyMessage="No damages reported"
          />
        </View>
      </View>
      <EditVehicleInformationModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={async (saved, exactMatch) => {
          // If exact match is found, call the ChangeVinOnVehicle endpoint
          if (exactMatch && workshopId && currentWorkshopSession?.workSessionId) {
            try {
              await changeVinOnVehicle({
                workshopId,
                workSessionId: currentWorkshopSession.workSessionId,
                vin: saved.vin,
                make: saved.make,
                model: saved.model,
                variant: saved.variant || '',
                variantId: saved.variantId || '',
                year: String(saved.year ?? ''),
              });
            } catch (error) {
              console.error('Failed to update vehicle VIN:', error);
            }
          }
          onSaved?.(saved);
        }}
        vin={displayVin}
        make={vehicle?.make}
        model={vehicle?.model}
        year={vehicle?.year}
        imageId={currentWorkshopSession?.vinImageId}
        vehicleVin={vin}
        workshopId={workshopId}
        workSessionId={currentWorkshopSession?.workSessionId}
      />
      <VinVerificationModal
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        onSaved={(saved) => {
          onSaved?.(saved);
          setUnmatchedVins((current) =>
            current.filter(
              (item) =>
                normalizeVin(item.vin) !== normalizeVin(saved.vin) &&
                normalizeVin(item.vin) !== normalizeVin(displayVin)
            )
          );
        }}
        vin={displayVin}
        make={vehicle?.make}
        model={vehicle?.model}
        year={vehicle?.year}
        imageId={currentWorkshopSession?.vinImageId}
        vehicleVin={vin}
      />
    </Box>
  );
};
